/**
 * List actions module
 * Handles dropdown toggling and list item actions (duplicate, delete)
 */

import { getCsrfToken } from './utils.js';

/**
 * Toggle a dropdown menu
 * @param {HTMLElement} button - The toggle button element
 */
export function toggleDropdown(button) {
    const dropdown = button.nextElementSibling;
    if (!dropdown) return;

    dropdown.classList.toggle('hidden');

    // Close when clicking outside
    function handleOutsideClick(event) {
        if (!button.contains(event.target) && !dropdown.contains(event.target)) {
            dropdown.classList.add('hidden');
            document.removeEventListener('click', handleOutsideClick);
        }
    }

    if (!dropdown.classList.contains('hidden')) {
        // Defer to avoid immediate close from the same click
        setTimeout(() => {
            document.addEventListener('click', handleOutsideClick);
        }, 0);
    }
}

/**
 * Submit a POST form to a given URL with CSRF token
 * @param {string} url - The action URL
 * @param {Object} extraFields - Additional hidden fields to include
 */
function submitPostForm(url, extraFields = {}) {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = url;

    const csrfToken = getCsrfToken();
    if (csrfToken) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'csrfmiddlewaretoken';
        input.value = csrfToken;
        form.appendChild(input);
    }

    Object.entries(extraFields).forEach(([name, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
}

/**
 * Initialize list actions event delegation
 */
export function init() {
    document.addEventListener('click', function (e) {
        // Dropdown toggle
        const dropdownToggle = e.target.closest('[data-toggle-dropdown]');
        if (dropdownToggle) {
            e.preventDefault();
            e.stopPropagation();
            toggleDropdown(dropdownToggle);
            return;
        }

        // Duplicate quote
        const duplicateBtn = e.target.closest('[data-duplicate-quote]');
        if (duplicateBtn) {
            e.preventDefault();
            const url = duplicateBtn.dataset.duplicateUrl;
            if (url) {
                submitPostForm(url);
            }
            return;
        }

        // Delete quote
        const deleteBtn = e.target.closest('[data-delete-quote]');
        if (deleteBtn) {
            e.preventDefault();
            const confirmMsg = deleteBtn.dataset.deleteConfirm;
            if (confirm(confirmMsg)) {
                const url = deleteBtn.dataset.deleteUrl;
                submitPostForm(url, {
                    '_method': 'DELETE',
                    'source': 'quotes_list',
                });
            }
            return;
        }
    });
}

// Expose globally for backward compatibility with inline onclick handlers
window.toggleDropdown = toggleDropdown;
